// Shrink a camera photo before it's stored: a 12 MP iPad photo is 3–5 MB,
// which fills device storage and is over Graph's 4 MB single-request upload.
// 2048 px on the long side is plenty to see defects on a wall.

const PHOTO_LONG_SIDE = 2048;
const THUMBNAIL_LONG_SIDE = 320;
const JPEG_QUALITY = 0.82;
const THUMBNAIL_QUALITY = 0.7;

function drawScaled(bitmap: ImageBitmap, longSide: number): HTMLCanvasElement {
  const scale = Math.min(1, longSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser can\'t process photos.');
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function toJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not save the photo.'))), 'image/jpeg', JPEG_QUALITY);
  });
}

/**
 * The stored photo, and its thumbnail as a data: URL (text — see DevicePhoto
 * in device-db.ts for why). createImageBitmap applies the camera's rotation.
 */
export async function preparePhoto(file: File): Promise<{ image: Blob; thumbnail: string; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  try {
    const full = drawScaled(bitmap, PHOTO_LONG_SIDE);
    return {
      image: await toJpegBlob(full),
      thumbnail: drawScaled(bitmap, THUMBNAIL_LONG_SIDE).toDataURL('image/jpeg', THUMBNAIL_QUALITY),
      // The size stored on SharePoint, for the photo's row.
      width: full.width,
      height: full.height,
    };
  } finally {
    bitmap.close();
  }
}

/** A thumbnail (data: URL) of a photo fetched from SharePoint, the same size as the device's own. */
export async function thumbnailOf(photo: Blob): Promise<string> {
  const bitmap = await createImageBitmap(photo);
  try {
    return drawScaled(bitmap, THUMBNAIL_LONG_SIDE).toDataURL('image/jpeg', THUMBNAIL_QUALITY);
  } finally {
    bitmap.close();
  }
}
